import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BookOpenCheck, Loader2, Mail } from "lucide-react";

export default function Login() {
  const { user, login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) navigate("/dashboard", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signin") {
        await login(form.email, form.password);
        toast.success("Welcome back");
      } else {
        await register(form.email, form.password, form.name);
        toast.success("Account created");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden overflow-hidden bg-[#0f172a] p-12 text-white lg:flex lg:flex-col lg:justify-between grain">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white text-[#0f172a]">
            <BookOpenCheck className="h-5 w-5" />
          </div>
          <span className="font-display text-xl">Publishing Coach</span>
        </div>
        <div>
          <h1 className="font-display text-4xl leading-tight tracking-tight">
            Your voice, verified in every language.
          </h1>
          <p className="mt-4 max-w-md text-slate-300">
            Sign in to turn a single manuscript into a catalog of KDP-ready EPUB files — each one faithful to your meaning, tone and facts.
          </p>
        </div>
        <p className="text-sm text-slate-400">Powered by RAVIRA API · 106 languages</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center bg-[#f6f5f1] px-5 py-12">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="w-full max-w-sm">
          <h2 className="font-display text-3xl tracking-tight">
            {mode === "signin" ? "Sign in" : "Create account"}
          </h2>
          <p className="mt-2 text-sm text-[#475569]">
            {mode === "signin" ? "Welcome back to your studio." : "Start your global catalog in minutes."}
          </p>

          <Button data-testid="google-login-btn" onClick={loginWithGoogle} variant="outline"
            className="mt-7 h-11 w-full rounded-xl border-[#e6e4dd] bg-white text-[#0f172a] hover:bg-slate-900 hover:text-white">
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path fill="currentColor" d="M12 11v2.8h6.6c-.3 1.7-1.9 5-6.6 5-4 0-7.2-3.3-7.2-7.4S8 4 12 4c2.3 0 3.8.9 4.6 1.7l2.3-2.2C17.5 2 15 1 12 1 5.9 1 1 5.9 1 12s4.9 11 11 11c6.4 0 10.6-4.5 10.6-10.8 0-.7-.1-1.2-.2-1.7H12z"/></svg>
            Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-3 text-xs text-[#94a3b8]">
            <div className="h-px flex-1 bg-[#e6e4dd]" /> OR <div className="h-px flex-1 bg-[#e6e4dd]" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Name</Label>
                <Input id="name" data-testid="name-input" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Author" className="h-11 rounded-xl bg-white" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="email-input" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com" className="h-11 rounded-xl bg-white" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="password-input" type="password" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••" className="h-11 rounded-xl bg-white" />
            </div>
            <Button data-testid="submit-auth-btn" type="submit" disabled={busy}
              className="h-11 w-full rounded-xl bg-[#0f172a] text-white hover:bg-[#1e293b]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="mr-2 h-4 w-4" />{mode === "signin" ? "Sign in" : "Create account"}</>}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-[#475569]">
            {mode === "signin" ? "New here? " : "Already have an account? "}
            <button data-testid="toggle-auth-mode-btn" onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
              className="font-semibold text-[#d97706] hover:underline">
              {mode === "signin" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
